import axios from 'axios';
import { QueryResponse, DashboardMetrics, KnowledgeStats, ConversationSession, InterviewSession, InterviewResponse, InterviewStatus, InterviewSummary } from '../types';
import { generateSQLFromQuery } from './ceoQueries';
import config from '../config';

const API_BASE_URL = config.api.baseUrl;
console.log('API: Base URL configured as:', API_BASE_URL);
console.log('API: Environment:', process.env.NODE_ENV);
console.log('API: Available env vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// In-memory admin token (set via Admin UI)
let adminToken: string | null = null;
export function setAdminToken(token: string | null) {
  adminToken = token;
  if (!token) return;
}

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('google_token');
  if (token) {
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      // Do not remove admin token here; it's a separate secret
      // Remove only Google token
      localStorage.removeItem('google_token');
      // Avoid reload loop in non-browser contexts
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

export interface CEOChatRequest {
  message: string;
  forExcel?: boolean;
  sessionId?: string;
  includeContext?: boolean;
}

export interface AdminKnowledgeDoc {
  id?: string;
  title: string;
  content: string;
  category?: string;
  keywords?: string[];
  source?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminStaticRule {
  id?: string;
  title: string;
  content: string;
  tags?: string[];
  type?: 'static_rule';
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminSearchQuery {
  q?: string;
  page?: number;
  size?: number;
}

export class APIService {
  // NEW: Conversational CEO Chat endpoint
  static async ceoChatConversational(message: string, sessionId: string, includeContext: boolean = true): Promise<QueryResponse> {
    try {
      const response = await api.post('/ceo-chat', { 
        message, 
        sessionId,
        includeContext 
      });
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        return this.getMockConversationalResponse(message, sessionId);
      }
      throw error;
    }
  }

  // NEW: Record successful query pattern for learning
  static async recordSuccess(sql: string, sessionId: string): Promise<void> {
    try {
      await api.post('/api/learn', { 
        sql, 
        sessionId, 
        success: true 
      });
    } catch (error) {
      console.warn('Failed to record success for learning:', error);
    }
  }

  // NEW: Get knowledge statistics
  static async getKnowledgeStats(): Promise<KnowledgeStats> {
    try {
      const response = await api.get('/api/knowledge-stats');
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch knowledge stats:', error);
      return this.getMockKnowledgeStats();
    }
  }

  // NEW: Get conversation session
  static async getConversationSession(sessionId: string): Promise<ConversationSession> {
    try {
      const response = await api.get(`/api/conversation/${sessionId}`);
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch conversation session:', error);
      return this.getMockConversationSession(sessionId);
    }
  }

  // NEW: Update conversation session
  static async updateConversationSession(sessionId: string, updates: Partial<ConversationSession>): Promise<void> {
    try {
      await api.put(`/api/conversation/${sessionId}`, updates);
    } catch (error) {
      console.warn('Failed to update conversation session:', error);
    }
  }

  // CEO Chat endpoint (new unified backend route)
  static async ceoChat(message: string, forExcel: boolean = true): Promise<QueryResponse> {
    try {
      const response = await api.post('/ceo-chat', { message, forExcel });
      // Expecting backend to return a shape compatible with QueryResponse
      return response.data;
    } catch (error) {
      // Fallback in dev
      if (process.env.NODE_ENV === 'development') {
        return this.getMockQueryResponse(message);
      }
      throw error;
    }
  }

  // NEW: Fast Chat endpoint for simple queries
  static async ceoChatFast(message: string): Promise<QueryResponse> {
    try {
      const response = await api.post('/ceo-chat-fast', { message });
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        return this.getMockQueryResponse(message);
      }
      throw error;
    }
  }

  // Smart routing function to determine which endpoint to use
  static async smartCeoChat(message: string, forExcel: boolean = true): Promise<QueryResponse> {
    // Check if this is a simple query that can use fast chat
    if (this.isSimpleQuery(message)) {
      console.log('API: Using fast chat for simple query:', message);
      return this.ceoChatFast(message);
    } else {
      console.log('API: Using full chat for complex query:', message);
      return this.ceoChat(message, forExcel);
    }
  }

  // Helper function to determine if a query is simple
  private static isSimpleQuery(message: string): boolean {
    const simpleKeywords = [
      'hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening',
      'time', 'date', 'status', 'help', 'test', 'ping', 'health',
      'how are you', 'what can you do', 'capabilities'
    ];
    
    const lowerMessage = message.toLowerCase().trim();
    return simpleKeywords.some(keyword => lowerMessage.includes(keyword));
  }

  // CEO Query endpoint (legacy; kept for compatibility)
  static async executeCEOQuery(query: string): Promise<QueryResponse> {
    try {
      const sql = generateSQLFromQuery(query);
      const response = await api.post('/api/ceo-query', {
        query,
        sql,
        requestSpreadsheet: true,
        includeAnalytics: true
      });
      return response.data;
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        return this.getMockQueryResponse(query);
      }
      throw error;
    }
  }

  // Dashboard metrics endpoint
  static async getDashboardMetrics(): Promise<DashboardMetrics> {
    try {
      console.log('API: Attempting to fetch dashboard metrics from:', `${API_BASE_URL}/api/dashboard-metrics`);
      const response = await api.get('/api/dashboard-metrics');
      console.log('API: Dashboard metrics response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API: Error fetching dashboard metrics:', error);
      // Always provide fallback data to prevent crashes
      console.log('API: Using mock data as fallback due to API error');
      return this.getMockDashboardMetrics();
    }
  }

  // Export data endpoint
  static async exportData(query: string, format: 'excel' | 'csv' | 'pdf' = 'excel'): Promise<Blob> {
    const response = await api.post(
      '/api/export',
      { query, format, includeMetadata: true, includeCharts: true },
      { responseType: 'blob' }
    );
    return response.data;
  }

  // Admin APIs: Knowledge (sleekshop-knowledge)
  static async adminCreateKnowledge(doc: AdminKnowledgeDoc) {
    const response = await api.post('/admin/knowledge', doc, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  static async adminSearchKnowledge(params: AdminSearchQuery) {
    const response = await api.get('/admin/knowledge', { params, headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {} });
    return response.data;
  }

  static async adminGetKnowledge(id: string) {
    const response = await api.get(`/admin/knowledge/${id}`, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  static async adminUpdateKnowledge(id: string, partial: Partial<AdminKnowledgeDoc>) {
    const response = await api.put(`/admin/knowledge/${id}`, partial, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  static async adminDeleteKnowledge(id: string) {
    const response = await api.delete(`/admin/knowledge/${id}`, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  // Admin APIs: Static Rules (sleekshop-knowledge-static)
  static async adminCreateStaticRule(rule: AdminStaticRule) {
    const response = await api.post('/admin/static', rule, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  static async adminListStaticRules(params: AdminSearchQuery) {
    const response = await api.get('/admin/static', { params, headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {} });
    return response.data;
  }

  static async adminGetStaticRule(id: string) {
    const response = await api.get(`/admin/static/${id}`, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  static async adminUpdateStaticRule(id: string, partial: Partial<AdminStaticRule>) {
    const response = await api.put(`/admin/static/${id}`, partial, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  static async adminDeleteStaticRule(id: string) {
    const response = await api.delete(`/admin/static/${id}`, {
      headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : {},
    });
    return response.data;
  }

  // Interview Mode API endpoints
  static async startInterview(userId: string = 'ceo'): Promise<InterviewSession> {
    try {
      const response = await api.post('/api/interview/start', { userId });
      return response.data;
    } catch (error) {
      console.error('Failed to start interview:', error);
      // Return mock interview session for development
      return this.getMockInterviewSession(userId);
    }
  }

  static async respondToInterview(sessionId: string, response: string): Promise<InterviewResponse> {
    try {
      const result = await api.post('/api/interview/respond', { sessionId, response });
      return result.data;
    } catch (error) {
      console.error('Failed to respond to interview:', error);
      // Return mock response for development
      return this.getMockInterviewResponse(sessionId, response);
    }
  }

  static async getInterviewStatus(sessionId: string): Promise<InterviewStatus> {
    try {
      const result = await api.get(`/api/interview/status/${sessionId}`);
      return result.data;
    } catch (error) {
      console.error('Failed to get interview status:', error);
      // Return mock status for development
      return this.getMockInterviewStatus(sessionId);
    }
  }

  static async endInterview(sessionId: string): Promise<InterviewSummary> {
    try {
      const result = await api.post('/api/interview/end', { sessionId });
      return result.data;
    } catch (error) {
      console.error('Failed to end interview:', error);
      // Return mock summary for development
      return this.getMockInterviewSummary(sessionId);
    }
  }

  // Mock data for development
  private static getMockQueryResponse(query: string): QueryResponse {
    const lowerQuery = query.toLowerCase();
    if (lowerQuery.includes('revenue')) {
      return {
        success: true,
        query,
        narrative: `Here's your revenue analysis for the last 30 days. Revenue has been trending upward with a 12% increase compared to the previous period.`,
        results: [
          { Date: '2024-01-01', Orders: 45, Revenue: 12500, AOV: 277.78 },
          { Date: '2024-01-02', Orders: 52, Revenue: 14800, AOV: 284.62 },
          { Date: '2024-01-03', Orders: 38, Revenue: 10200, AOV: 268.42 },
          { Date: '2024-01-04', Orders: 61, Revenue: 17500, AOV: 286.89 },
          { Date: '2024-01-05', Orders: 49, Revenue: 13800, AOV: 281.63 }
        ],
        sql: generateSQLFromQuery(query),
        exportReady: true
      };
    }
    if (lowerQuery.includes('problem') || lowerQuery.includes('issue')) {
      return {
        success: true,
        query,
        narrative: `I found ${5} orders that need attention. These orders are either stuck in processing or missing tracking information.`,
        results: [
          { OrderNumber: 'ORD-001', OrderDate: '2024-01-01', CustomerEmail: 'customer1@example.com', FinalGrandTotal: 299.99, Issue: 'Stuck in Packing' },
          { OrderNumber: 'ORD-002', OrderDate: '2024-01-02', CustomerEmail: 'customer2@example.com', FinalGrandTotal: 149.99, Issue: 'Not Processed' },
          { OrderNumber: 'ORD-003', OrderDate: '2024-01-03', CustomerEmail: 'customer3@example.com', FinalGrandTotal: 399.99, Issue: 'Stuck in Packing' }
        ],
        sql: generateSQLFromQuery(query),
        exportReady: true
      };
    }
    if (lowerQuery.includes('stock') || lowerQuery.includes('inventory')) {
      return {
        success: true,
        query,
        narrative: `Inventory analysis shows ${3} products are running low on stock and need immediate attention.`,
        results: [
          { SKU: 'SKU-001', ProductName: 'Premium Widget', CurrentStock: 5, ReorderPoint: 10, Avg30DaySales: 15, StockStatus: 'CRITICAL' },
          { SKU: 'SKU-002', ProductName: 'Deluxe Gadget', CurrentStock: 12, ReorderPoint: 15, Avg30DaySales: 8, StockStatus: 'WARNING' },
          { SKU: 'SKU-003', ProductName: 'Super Tool', CurrentStock: 3, ReorderPoint: 8, Avg30DaySales: 12, StockStatus: 'CRITICAL' }
        ],
        sql: generateSQLFromQuery(query),
        exportReady: true
      };
    }
    return {
      success: true,
      query,
      narrative: `I've processed your query: "${query}". Here are the results based on the latest data.`,
      results: [
        { OrderNumber: 'ORD-001', OrderDate: '2024-01-01', CustomerEmail: 'customer1@example.com', FinalGrandTotal: 299.99 },
        { OrderNumber: 'ORD-002', OrderDate: '2024-01-02', CustomerEmail: 'customer2@example.com', FinalGrandTotal: 149.99 },
        { OrderNumber: 'ORD-003', OrderDate: '2024-01-03', CustomerEmail: 'customer3@example.com', FinalGrandTotal: 399.99 }
      ],
      sql: generateSQLFromQuery(query),
      exportReady: true
    };
  }

  // NEW: Mock conversational response for development
  private static getMockConversationalResponse(message: string, sessionId: string): QueryResponse {
    const lowerMessage = message.toLowerCase();
    
    // Simulate clarification requests for certain queries
    if (lowerMessage.includes('sales') && !lowerMessage.includes('revenue')) {
      return {
        success: false,
        query: message,
        narrative: '',
        results: [],
        exportReady: false,
        needsClarification: true,
        question: "I see you're asking about sales. Do you mean Orders or Order Details? This helps me give you the most accurate information.",
        sessionId
      };
    }
    
    if (lowerMessage.includes('target') && !lowerMessage.includes('cart')) {
      return {
        success: false,
        query: message,
        narrative: '',
        results: [],
        exportReady: false,
        needsClarification: true,
        question: "I found multiple references to 'Target' in your database. Are you looking for Target store performance (CartID 24) or Target product categories?",
        sessionId
      };
    }
    
    // Simulate successful responses with learning progress
    return {
      success: true,
      query: message,
      narrative: `I've processed your query: "${message}". Here are the results based on the latest data.`,
      results: [
        { OrderNumber: 'ORD-001', OrderDate: '2024-01-01', CustomerEmail: 'customer1@example.com', FinalGrandTotal: 299.99 },
        { OrderNumber: 'ORD-002', OrderDate: '2024-01-02', CustomerEmail: 'customer2@example.com', FinalGrandTotal: 149.99 },
        { OrderNumber: 'ORD-003', OrderDate: '2024-01-03', CustomerEmail: 'customer3@example.com', FinalGrandTotal: 399.99 }
      ],
      sql: generateSQLFromQuery(message),
      exportReady: true,
      sessionId,
      learningProgress: {
        tablesLearned: 15,
        totalTables: 447,
        businessRules: 8,
        successRate: 87,
        queriesToday: 12,
        lastLearningUpdate: new Date()
      }
    };
  }

  // NEW: Mock knowledge stats for development
  private static getMockKnowledgeStats(): KnowledgeStats {
    return {
      tablesLearned: 15,
      totalTables: 447,
      businessRules: 8,
      successRate: 87,
      queriesToday: 12,
      recentQueries: [
        "Show me today's revenue",
        "What are our top products?",
        "Compare this week to last week",
        "Show me problem orders"
      ],
      commonPatterns: [
        "Revenue analysis by date range",
        "Product performance comparison",
        "Order status tracking",
        "Channel performance breakdown"
      ]
    };
  }

  // NEW: Mock conversation session for development
  private static getMockConversationSession(sessionId: string): ConversationSession {
    return {
      sessionId,
      messages: [],
      isLearning: false,
      learningProgress: {
        tablesLearned: 15,
        totalTables: 447,
        businessRules: 8,
        successRate: 87,
        queriesToday: 12,
        lastLearningUpdate: new Date()
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };
  }

  private static getMockDashboardMetrics(): DashboardMetrics {
    return {
      todayRevenue: 12500,
      weekRevenue: 87500,
      monthRevenue: 325000,
      pendingOrders: 23,
      channelBreakdown: [
        { channel: 'Amazon', orders: 150, revenue: 45000, aov: 300, revenuePercent: 45 },
        { channel: 'Direct', orders: 80, revenue: 24000, aov: 300, revenuePercent: 24 },
        { channel: 'Target', orders: 60, revenue: 18000, aov: 300, revenuePercent: 18 },
        { channel: 'Walmart', orders: 40, revenue: 12000, aov: 300, revenuePercent: 12 },
        { channel: 'Other', orders: 10, revenue: 3000, aov: 300, revenuePercent: 3 }
      ],
      revenueTrend: [
        { date: '2024-01-01', revenue: 12500, orders: 45, aov: 277.78 },
        { date: '2024-01-02', revenue: 14800, orders: 52, aov: 284.62 },
        { date: '2024-01-03', revenue: 10200, orders: 38, aov: 268.42 },
        { date: '2024-01-04', revenue: 17500, orders: 61, aov: 286.89 },
        { date: '2024-01-05', revenue: 13800, orders: 49, aov: 281.63 }
      ]
    };
  }

  private static getMockInterviewSession(userId: string): InterviewSession {
    return {
      sessionId: `mock-${Date.now()}`,
      userId,
      phase: 'introduction',
      progress: 0,
      currentQuestion: "Hi Andrew! I'm here to learn about your Sleekshop database so I can better help with business questions. As a beauty retailer with multiple sales channels, what would you say is your most important table?",
      questionsAsked: 0,
      totalQuestions: 15,
      factsLearned: [],
      tablesDiscovered: [],
      relationshipsLearned: [],
      businessRules: [],
      startedAt: new Date(),
      lastActivity: new Date(),
      isActive: true
    };
  }

  private static getMockInterviewResponse(sessionId: string, response: string): InterviewResponse {
    const mockQuestions = [
      "That makes perfect sense for a multi-channel retailer! I can see the Orders table is central to your operations. How do you track which channel each order came from? Is there a specific field that identifies whether an order is from Target vs Amazon vs your direct site?",
      "Excellent! So CartID is your channel identifier. Now I'm curious about your order lifecycle - you mentioned orders from multiple channels, but how do you track when an order moves from 'placed' to 'ready to ship'? Do you have status fields or separate tables that handle this workflow?",
      "Perfect! So you have a workflow from Orders → Packing → Tracking. This is great for understanding your fulfillment process. Now, let me ask about your products - I see you mentioned 'kits' earlier. How do you handle products that are made up of multiple components? Do you have a separate table for kit contents?"
    ];
    
    return {
      sessionId,
      question: mockQuestions[Math.floor(Math.random() * mockQuestions.length)],
      phase: 'core_tables',
      progress: Math.min(100, Math.random() * 30 + 20),
      learnedFromResponse: [
        `Learned about ${response.length > 20 ? 'detailed business process' : 'business insight'}`,
        "Understanding of operational workflow"
      ],
      conversationInsights: [
        "User has clear understanding of business operations",
        "Multi-channel retail experience evident"
      ],
      nextPhase: 'relationships',
      suggestions: [
        "Consider asking about inventory management",
        "Explore customer relationship patterns"
      ],
      followUpQuestions: [
        "How do you handle inventory across different channels?",
        "What customer insights do you track?"
      ]
    };
  }

  private static getMockInterviewStatus(sessionId: string): InterviewStatus {
    return {
      sessionId,
      phase: 'core_tables',
      progress: 35,
      insights: [
        "Orders table is central to operations",
        "Multi-channel sales through CartID field",
        "Workflow: Orders → Packing → Tracking"
      ],
      lastQuestion: "How do you handle products that are made up of multiple components?",
      factsLearned: [
        "Orders table tracks all purchases",
        "CartID identifies sales channel",
        "Fulfillment workflow exists"
      ],
      tablesDiscovered: ["Orders", "Packing", "Tracking"],
      relationshipsLearned: [
        "Orders → Packing (one-to-one)",
        "Orders → Tracking (one-to-one)"
      ],
      businessRules: [
        "All orders must have channel identification",
        "Orders follow fulfillment workflow"
      ],
      estimatedTimeRemaining: 8
    };
  }

  private static getMockInterviewSummary(sessionId: string): InterviewSummary {
    return {
      sessionId,
      summary: "Successfully learned about SleekShop's core business operations and database structure through natural conversation.",
      factsLearned: [
        "Orders table is the central hub for all business operations",
        "Multi-channel sales through Target, Amazon, Walmart, and direct website",
        "CartID field identifies sales channel (Target=24, Amazon=2, Direct=11, Walmart=10)",
        "Fulfillment workflow: Orders → Packing → Tracking",
        "Kit products with component tracking capabilities",
        "Beauty industry specific considerations (hazmat, ingredients)"
      ],
      tablesDiscovered: ["Orders", "Packing", "Tracking", "Products", "Order Details", "Inventory"],
      relationshipsLearned: [
        "Orders → Packing (one-to-one relationship)",
        "Orders → Tracking (one-to-one relationship)",
        "Orders → Order Details (one-to-many relationship)",
        "Products → Order Details (one-to-many relationship)"
      ],
      businessRules: [
        "All orders must have channel identification through CartID",
        "Orders follow sequential fulfillment workflow",
        "Kit products require component-level tracking",
        "Multi-channel inventory synchronization is critical"
      ],
      totalInsights: 15,
      confidenceScore: 0.92,
      recommendations: [
        "Consider implementing real-time inventory sync across channels",
        "Explore customer behavior patterns for targeted marketing",
        "Implement automated alerts for fulfillment bottlenecks"
      ],
      nextSteps: [
        "Continue learning through regular business questions",
        "Ask about specific operational challenges",
        "Explore advanced analytics capabilities"
      ]
    };
  }
}

export default APIService;